/**
 * Trunnion AI - Execution Monitor
 * Top-level execution monitoring view with timeline, tool calls, and reasoning trace.
 */

import { useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Brain, Wrench, BarChart3, RotateCcw } from 'lucide-react';
import { ExecutionTimeline } from './ExecutionTimeline';
import { ToolCallCard } from './ToolCallCard';
import { ReasoningTraceViewer } from './ReasoningTraceViewer';
import { TokenUsageMeter } from './TokenUsageMeter';
import { ExecutionControls } from './ExecutionControls';
import { ExecutionReplay } from './ExecutionReplay';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { EXECUTION_STATUS_CONFIG } from '@/config/constants';
import type { Execution } from '@/types';

interface ExecutionMonitorProps {
  execution: Execution;
  onCancel?: () => void;
  onRetry?: () => void;
}

type DetailTab = 'reasoning' | 'tools' | 'tokens';

export function ExecutionMonitor({ execution, onCancel, onRetry }: ExecutionMonitorProps) {
  const [selectedStepId, setSelectedStepId] = useState<string | null>(
    execution.steps[0]?.id ?? null,
  );
  const [activeTab, setActiveTab] = useState<DetailTab>('reasoning');
  const [showReplay, setShowReplay] = useState(false);

  const selectedStep = execution.steps.find((s) => s.id === selectedStepId);
  const statusConfig = EXECUTION_STATUS_CONFIG[execution.status];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-trunnion-slate-700 bg-trunnion-slate-900 px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-white">
            {execution.workflowName}
          </h2>
          <ClassificationTag level={execution.classification} />
          <Badge
            variant={
              execution.status === 'completed'
                ? 'success'
                : execution.status === 'failed'
                  ? 'error'
                  : execution.status === 'running'
                    ? 'info'
                    : 'default'
            }
            dot
          >
            {statusConfig?.label ?? execution.status}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <ExecutionControls
            executionId={execution.id}
            status={execution.status}
            onCancel={onCancel}
            onRetry={onRetry}
          />
          {(execution.status === 'completed' || execution.status === 'failed') && (
            <Button
              variant="ghost"
              size="sm"
              icon={<RotateCcw className="h-4 w-4" />}
              onClick={() => setShowReplay(!showReplay)}
            >
              {showReplay ? 'Monitor' : 'Replay'}
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {showReplay ? (
        <ExecutionReplay execution={execution} />
      ) : (
      <PanelGroup direction="horizontal" className="flex-1">
        {/* Timeline Panel */}
        <Panel defaultSize={30} minSize={20}>
          <div className="flex flex-col h-full border-r border-trunnion-slate-700">
            <div className="border-b border-trunnion-slate-700 px-4 py-2">
              <h3 className="text-sm font-medium text-trunnion-slate-300">
                Execution Steps
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <ExecutionTimeline
                steps={execution.steps}
                selectedStepId={selectedStepId}
                onStepSelect={setSelectedStepId}
              />
            </div>
          </div>
        </Panel>

        <PanelResizeHandle className="w-1 bg-trunnion-slate-700 hover:bg-trunnion-gold-500/30 transition-colors" />

        {/* Detail Panel */}
        <Panel defaultSize={70} minSize={40}>
          <div className="flex flex-col h-full">
            {/* Tab bar */}
            <div className="flex border-b border-trunnion-slate-700 bg-trunnion-slate-900">
              {[
                { key: 'reasoning' as const, icon: Brain, label: 'Reasoning' },
                { key: 'tools' as const, icon: Wrench, label: 'Tool Calls' },
                { key: 'tokens' as const, icon: BarChart3, label: 'Tokens' },
              ].map(({ key, icon: Icon, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === key
                      ? 'border-trunnion-gold-500 text-trunnion-gold-500'
                      : 'border-transparent text-trunnion-slate-400 hover:text-white'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-4">
              {!selectedStep ? (
                <div className="flex items-center justify-center h-full text-sm text-trunnion-slate-400">
                  Select a step to view details
                </div>
              ) : (
                <>
                  {activeTab === 'reasoning' && (
                    <ReasoningTraceViewer
                      entries={selectedStep.reasoningTrace}
                    />
                  )}
                  {activeTab === 'tools' && (
                    <div className="space-y-3">
                      {selectedStep.toolCalls.length === 0 ? (
                        <p className="text-center py-8 text-sm text-trunnion-slate-400">
                          No tool calls for this step
                        </p>
                      ) : (
                        selectedStep.toolCalls.map((tc) => (
                          <ToolCallCard key={tc.id} toolCall={tc} />
                        ))
                      )}
                    </div>
                  )}
                  {activeTab === 'tokens' && (
                    <TokenUsageMeter steps={execution.steps} />
                  )}
                </>
              )}
            </div>
          </div>
        </Panel>
      </PanelGroup>
      )}
    </div>
  );
}
